import React, { useCallback } from 'react';
import { useIntl } from 'react-intl';
import { COVER_DOC_LIMITS, type CoverTextBox } from 'fym-shared/src/music24/coverDoc';
import { TypedMemo } from '../../common/types';
import { useCoverEditor } from '../CoverEditorContext';
import { coverEditorMessages } from '../coverEditorMessages';
import { themeTextStyle } from '../doc/textTheme';
import { TextLookFields } from './TextLookFields';
import { TextStyleFields } from './TextStyleFields';
import { useTextPatch } from './useTextPatch';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the cover editor; each part uses its own slice of it */
import styles from '../CoverEditor.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface TextInspectorProps {
  box: CoverTextBox;
}

const ROLE_MESSAGES = {
  title: coverEditorMessages.roleTitle,
  artist: coverEditorMessages.roleArtist,
  free: coverEditorMessages.roleFree,
} as const;

/**
 * The inspector of the selected text box (design cover-editor b1, d1): the words, the style
 * block, the look block, and "Reset to theme" (the font, the colour and the shadow of the release
 * theme; the place and the size stay).
 */
const TextInspectorComponent: React.FC<TextInspectorProps> = ({ box }) => {
  const intl = useIntl();
  const { state, readOnly } = useCoverEditor();
  const patch = useTextPatch(box.id);
  const changeText = useCallback(
    (event: React.ChangeEvent<HTMLTextAreaElement>) =>
      patch({ text: event.target.value.slice(0, COVER_DOC_LIMITS.textLength) }, 'text'),
    [patch]
  );
  const reset = useCallback(() => {
    const style = themeTextStyle(box.role, state.doc.themeId, state.doc.palette);
    if (style !== null) {
      patch(style);
    }
  }, [box.role, patch, state.doc.palette, state.doc.themeId]);

  return (
    <>
      <h4 className={styles.inspectorHead}>
        {intl.formatMessage(coverEditorMessages.inspectorText)}
        <span className={styles.inspectorSub}>{intl.formatMessage(ROLE_MESSAGES[box.role])}</span>
        <span className={styles.grow} />
        <button type="button" className={styles.linkButton} onClick={reset} disabled={readOnly}>
          {intl.formatMessage(coverEditorMessages.resetToTheme)}
        </button>
      </h4>
      <textarea
        className={styles.textArea}
        rows={2}
        value={box.text}
        disabled={readOnly}
        aria-label={intl.formatMessage(coverEditorMessages.textEditLabel)}
        onChange={changeText}
      />
      <TextStyleFields box={box} />
      <div className={styles.separator} />
      <TextLookFields box={box} />
      <span className={styles.grow} />
      <span className={styles.hint}>{intl.formatMessage(coverEditorMessages.textHint)}</span>
    </>
  );
};

export const TextInspector = TypedMemo(TextInspectorComponent);
